import { useSearchParams, useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle, ArrowRight, Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEffect, useState, useRef } from "react";
import { handleSubmitDeal } from "@/services/cim-generation.service";
import { useToast } from "@/hooks/use-toast";
import { sendWelcomeEmail } from "@/lib/send-welcome-email";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app, db } from "@/lib/firebase";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";

type SessionInfo = {
  userType: "buyer" | "seller" | "broker" | "consultant" | "partner";
  tier: string | null;
  paymentStep: "pre_evaluation" | "publication" | "subscription" | null;
  planName: string;
  amountPaid: number;
  amountSubtotal: number;
  discount: number;
  currency: string;
  transactionId: string;
  customerEmail: string | null;
  customerName: string | null;
  billingAddress: string | null;
};

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { language: uiLanguage } = useLanguage();
  const { user } = useAuth();
  const { toast } = useToast();
  const [dealSubmitting, setDealSubmitting] = useState(false);
  const [dealSubmitted, setDealSubmitted] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendStatus, setResendStatus] = useState<"idle" | "pending" | "success" | "error">("idle");
  const [resendMessage, setResendMessage] = useState<string>("");
  const [cooldown, setCooldown] = useState(0);
  const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null);
  const [preferredLanguage, setPreferredLanguage] = useState<"en" | "fr">("en");
  const submittedRef = useRef(false);

  const RESEND_COOLDOWN_SECONDS = 60;
  const STORAGE_KEY = `welcomeEmailResend:${user?.email || "anon"}`;

  // Fallback query params (used only if Stripe session lookup fails)
  const fallbackUserType =
  (searchParams.get("userType") || searchParams.get("type") || searchParams.get("role")) as SessionInfo["userType"] | null;
  const fallbackPaymentStep = searchParams.get("step") as SessionInfo["paymentStep"];
  const fallbackTier = searchParams.get("tier");
  const sessionId = searchParams.get("session_id");

  // Display values — Stripe session is source of truth, fallback to query params
  const userType: SessionInfo["userType"] = sessionInfo?.userType || fallbackUserType || "buyer";
  const paymentStep = sessionInfo?.paymentStep || fallbackPaymentStep || null;
  const tier = fallbackTier || sessionInfo?.tier;
  const TIER_NAME_MAP: Record<string, string> = {
    essential:        'Essential',
    essentialPlus:    'Essential Plus',
    essential_plus:   'Essential Plus',
    acquisitionPro:   'Acquisition Pro',
    acquisition_pro:  'Acquisition Pro',
    byod:             'BYOD',
    byod_default:     'BYOD',
    byod_essential:   'BYOD Essential',
    byod_essential_plus: 'BYOD Essential Plus',
    consultant_explorer: 'Consultant Explorer',
    consultant:       'Consultant Pro',
    consultant_pro:   'Consultant Pro',
    consultant_elite: 'Consultant Elite',
    partner_basic:    'Internal Partner Basic',
    partner_pro:      'Internal Partner Pro',
    partner_elite:    'Internal Partner Elite',
  };
  // Tier from URL takes precedence so the title matches the plan the user picked,
  // even when the backend returns a generic planName like "Essential".
  const planName =
    TIER_NAME_MAP[tier as string] ||
    sessionInfo?.planName ||
    (userType === 'consultant' ? 'Consultant Program' :
     userType === 'buyer'      ? 'Essential' :
     paymentStep === 'publication' ? 'Publication' : 'Pre-evaluation');
  const amountPaid = sessionInfo?.amountPaid ?? null;
  const currency = sessionInfo?.currency || "USD";

  // 1. Fetch real Stripe session info (source of truth for amount + plan)
  useEffect(() => {
    if (!sessionId || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const fn = httpsCallable<{ sessionId: string }, SessionInfo>(
          getFunctions(app, "us-central1"),
          "getCheckoutSessionInfo",
        );
        const { data } = await fn({ sessionId });
        if (!cancelled) {
          setSessionInfo(data);
          console.log("[PaymentSuccess] Stripe session info loaded", data);
        }
      } catch (err) {
        console.warn("[PaymentSuccess] Could not fetch Stripe session info — using URL fallback", err);
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId, user]);

  // 2. Resolve user's preferred language from Firestore profile (default English)
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "userProfiles", user.id));
        const pref = snap.exists() ? snap.data()?.preferredLanguage : null;
        if (!cancelled) setPreferredLanguage(pref === "fr" ? "fr" : "en");
      } catch (err) {
        console.warn("[PaymentSuccess] Could not read profile preferredLanguage — defaulting to en", err);
        if (!cancelled) setPreferredLanguage("en");
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  // Restore cooldown + last status from localStorage on mount / user change
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as {
        cooldownUntil?: number;
        status?: "idle" | "pending" | "success" | "error";
        message?: string;
      };
      if (parsed.cooldownUntil && parsed.cooldownUntil > Date.now()) {
        setCooldown(Math.ceil((parsed.cooldownUntil - Date.now()) / 1000));
      }
      if (parsed.status && parsed.status !== "pending") {
        setResendStatus(parsed.status);
        setResendMessage(parsed.message || "");
      }
    } catch (e) {
      console.warn("[PaymentSuccess] Failed to restore resend state", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  // ─────────────────────────────────────────────────────────────────────
  // NOTE (correctif du 5 août 2026) : l'envoi automatique du courriel de
  // bienvenue/reçu a été retiré de ce composant. Il faisait doublon avec
  // l'envoi déjà effectué côté backend par le webhook Stripe
  // (PaymentsService.handleWebhook → EmailService.sendWelcomeEmail avec
  // showInvoiceNotice: true), qui dispose des vraies données de paiement.
  //
  // Le fallback client tombait systématiquement sur un envoi générique
  // sans reçu (rôle "buyer" par défaut, aucun planName/totalPaid) parce
  // que les successUrl de DealCheckout.tsx et CIMApprovalBanner.tsx
  // n'incluent pas ?session_id={CHECKOUT_SESSION_ID} — sessionInfo restait
  // donc toujours null ici, peu importe le vrai rôle du payeur.
  //
  // Le bouton "Renvoyer le courriel" plus bas reste actif : il sert de
  // filet de sécurité manuel si l'envoi backend échoue, sans dupliquer
  // automatiquement l'envoi à chaque chargement de cette page.
  // ─────────────────────────────────────────────────────────────────────

  // Cooldown countdown
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const persistResendState = (
    status: "idle" | "pending" | "success" | "error",
    message: string,
    cooldownSeconds: number,
  ) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          status,
          message,
          cooldownUntil: cooldownSeconds > 0 ? Date.now() + cooldownSeconds * 1000 : 0,
        }),
      );
    } catch (e) {
      console.warn("[PaymentSuccess] Failed to persist resend state", e);
    }
  };

  const handleResendEmail = async () => {
    if (resending || cooldown > 0) return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as { cooldownUntil?: number };
        if (parsed.cooldownUntil && parsed.cooldownUntil > Date.now()) {
          setCooldown(Math.ceil((parsed.cooldownUntil - Date.now()) / 1000));
          return;
        }
      }
    } catch { /* ignore */ }

    if (!user?.email) {
      toast({
        title: preferredLanguage === "fr" ? "Aucun compte détecté" : "No account detected",
        description: preferredLanguage === "fr"
          ? "Connectez-vous pour renvoyer le courriel de confirmation."
          : "Please log in to resend the confirmation email.",
        variant: "destructive",
      });
      return;
    }

    const role = sessionInfo?.userType || fallbackUserType || "buyer";
    const [firstName, ...rest] = (user.displayName || user.email.split("@")[0]).split(" ");
    const lastName = rest.join(" ");

    try {
      setResending(true);
      setResendStatus("pending");
      setResendMessage(preferredLanguage === "fr" ? "Envoi en cours..." : "Sending...");

      const payload = sessionInfo
        ? {
            email: user.email,
            firstName: firstName || "",
            lastName: lastName || "",
            role,
            language: preferredLanguage,
            showInvoiceNotice: true,
            planName: sessionInfo.planName,
            totalPaid: sessionInfo.amountPaid,
            basePrice: sessionInfo.amountSubtotal,
            discount: sessionInfo.discount,
            currency: sessionInfo.currency,
            transactionId: sessionInfo.transactionId,
            billingAddress: sessionInfo.billingAddress || undefined,
            customerName: sessionInfo.customerName || undefined,
          }
        : {
            email: user.email,
            firstName: firstName || "",
            lastName: lastName || "",
            role,
            language: preferredLanguage,
          };

      const res = (await sendWelcomeEmail(payload)) as { emailId?: string } | undefined;
      const emailId = res?.emailId;
      const successMsg = preferredLanguage === "fr"
        ? `Courriel envoyé à ${user.email}${emailId ? ` (id: ${emailId})` : ""}.`
        : `Email sent to ${user.email}${emailId ? ` (id: ${emailId})` : ""}.`;
      setResendStatus("success");
      setResendMessage(successMsg);
      toast({
        title: preferredLanguage === "fr" ? "Courriel renvoyé" : "Email resent",
        description: preferredLanguage === "fr"
          ? `Un nouveau courriel a été envoyé à ${user.email}.`
          : `A new confirmation email was sent to ${user.email}.`,
      });
      setCooldown(RESEND_COOLDOWN_SECONDS);
      persistResendState("success", successMsg, RESEND_COOLDOWN_SECONDS);
    } catch (err: any) {
      const errMsg = err?.message || (preferredLanguage === "fr" ? "Erreur inconnue" : "Unknown error");
      setResendStatus("error");
      setResendMessage(errMsg);
      toast({
        title: preferredLanguage === "fr" ? "Échec de l'envoi" : "Send failed",
        description: errMsg,
        variant: "destructive",
      });
      const serverRetry = Number(err?.details?.retryIn);
      const cooldownSec = err?.code === "functions/resource-exhausted" || err?.code === "resource-exhausted"
        ? (Number.isFinite(serverRetry) && serverRetry > 0 ? serverRetry : 60)
        : 15;
      setCooldown(cooldownSec);
      persistResendState("error", errMsg, cooldownSec);
    } finally {
      setResending(false);
    }
  };

  // Auto-submit deal after seller/broker pre-evaluation payment
  useEffect(() => {
    if (submittedRef.current || dealSubmitted || !user) return;
    if (paymentStep !== "pre_evaluation") return;

    const pendingData = localStorage.getItem("dealSubmissionPending");
    if (!pendingData) return;

   submittedRef.current = true;

const submitDeal = async () => {
  try {
    setDealSubmitting(true);
    const { formData, userRole, planId, analyzedDocs, uploadedFiles, brokerFormId } = JSON.parse(pendingData);
    const sessionId = searchParams.get("session_id") || undefined;
   
    // CORRIGÉ (28 août 2026) — priorité à user.roles (compte réel
    // connecté) plutôt qu'à userRole/userType (localStorage/URL), qui
    // peuvent être absents ou désynchronisés. Même défaut que celui
    // corrigé dans DealCheckout.tsx (resolveUserRole()) : faire confiance
    // aveuglément à une valeur stockée plutôt qu'au compte connecté
    // causait "Go to Seller Dashboard" affiché à un broker qui venait de
    // payer, avec un texte orienté acheteur.
    const accountRoles: string[] = Array.isArray((user as any)?.roles) ? (user as any).roles : [];
    const role = accountRoles.includes('broker') ? 'broker'
      : accountRoles.includes('seller') ? 'seller'
      : accountRoles.includes('buyer') ? 'buyer'
      : (userRole || userType || 'seller');

    // Combine les documents analysés par l'IA et les uploads manuels
    // (documents + photos) en une seule liste pour le backend.
    const manualDocs = [
      ...(uploadedFiles?.documents || []),
      ...(uploadedFiles?.photos || []),
    ]
      .filter((f: any) => f?.path && f?.url)
      .map((f: any) => ({ name: f.name, mimeType: f.type, url: f.url, path: f.path }));

    const combinedDocs = [...(analyzedDocs || []), ...manualDocs];

    // Appel NestJS POST /deals/submit
    const { postApi } = await import('@/lib/api');
    const result = await postApi('/deals/submit', {
      formData,
      userId: user.id,
      userEmail: user.email || '',
      userRole: role,
      stripeSessionId: sessionId,
      planId: planId || 'seller_pre_evaluation',
      language: preferredLanguage,
      analyzedDocs: combinedDocs,
    });
    
    if (result?.success) {
      localStorage.removeItem("dealSubmissionPending");
      localStorage.removeItem("dealSubmissionDraft");

      // AJOUTÉ — le deal est maintenant réellement créé (dealSubmissions
      // + miroir 'deals'). Si ce formulaire provenait d'une entrée
      // representedSellers/{repId}/dealForms/{brokerFormId} (broker qui a
      // rempli/repris un formulaire via brokerMode dans SubmitDealForm.tsx),
      // cette entrée n'a plus lieu de figurer comme "en cours" — on la
      // marque finalisée, avec un lien vers le vrai deal créé. Si cette
      // entrée était elle-même une copie d'un formulaire soumis par le
      // vendeur (copiedFromFormId, voir BrokerDashboard.tsx
      // handleOpenSellerForm), l'originale est marquée finalisée aussi,
      // pour que les deux disparaissent ensemble de la liste "en cours".
      const repId = formData?.general?.representedSellerId;
      if (repId && brokerFormId) {
        try {
          const entryRef = doc(db, 'representedSellers', repId, 'dealForms', brokerFormId);
          const entrySnap = await getDoc(entryRef);
          if (entrySnap.exists()) {
            const entryData: any = entrySnap.data();
            await updateDoc(entryRef, {
              finalizedDealId: result.dealId,
              finalizedAt: serverTimestamp(),
            });
            if (entryData.copiedFromFormId) {
              await updateDoc(doc(db, 'representedSellers', repId, 'dealForms', entryData.copiedFromFormId), {
                finalizedDealId: result.dealId,
                finalizedAt: serverTimestamp(),
              }).catch(() => undefined);
            }
          }
        } catch (cleanupErr) {
          console.warn('[PaymentSuccess] Failed to finalize dealForms entry', cleanupErr);
        }
      }
      // Nettoyage des anciens champs singuliers hérités (avant la
      // sous-collection dealForms), au cas où un vendeur en aurait encore.
      if (repId) {
        try {
          const sellerRef = doc(db, 'representedSellers', repId);
          const sellerSnap = await getDoc(sellerRef);
          if (sellerSnap.exists() && sellerSnap.data()?.brokerDraftToken) {
            await updateDoc(sellerRef, { brokerDraftToken: null, brokerDraftStatus: null });
          }
        } catch (cleanupErr) {
          console.warn('[PaymentSuccess] Failed to clear legacy broker draft fields', cleanupErr);
        }
      }

      setDealSubmitted(true);
      toast({        title: uiLanguage === "fr"
          ? `Deal soumis! Référence: ${result.referenceNumber}`
          : `Deal submitted! Reference: ${result.referenceNumber}`,
        description: uiLanguage === "fr"
          ? "Votre dossier est en révision. Courriel de confirmation envoyé."
          : "Your submission is under review. Confirmation email sent.",
      });
    } else {
      throw new Error('Submission failed');
    }
  } catch (err) {
    console.error("Failed to submit deal after payment:", err);
    toast({
      title: uiLanguage === "fr" ? "Erreur de soumission" : "Submission error",
      description: uiLanguage === "fr"
        ? "Paiement traité mais soumission échouée. Contactez le support."
        : "Payment processed but submission failed. Contact support.",
      variant: "destructive",
    });
  } finally {
    setDealSubmitting(false);
  }
};

submitDeal();
  }, [user, paymentStep, dealSubmitted, uiLanguage, toast]);

   const getDashboardPath = () => {
    if (role === "consultant") return "/dashboard/consultant";
    if (role === "broker") return "/dashboard/broker";
    if (role === "partner") return "/internal-partners";
    if (role === "buyer") return "/buyer/dashboard";
    // Detect partner from planName
    if (planName?.includes("Internal Partner")) return "/internal-partners";
    return "/seller/dashboard";
  };

  const formatAmount = (n: number) =>
    `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;

  const getTitle = () => {
    if (userType === "buyer") {
      return uiLanguage === "fr"
        ? `Félicitations ! Abonnement ${planName} activé !`
        : `Congratulations! ${planName} subscription activated!`;
    }
    if (userType === "consultant") {
      return uiLanguage === "fr"
        ? "Félicitations ! Abonnement activé !"
        : "Congratulations! Subscription activated!";
    }
    if (uiLanguage === "fr") {
      return paymentStep === "pre_evaluation"
        ? "Paiement de pré-évaluation réussi !"
        : "Paiement de publication réussi !";
    }
    return paymentStep === "pre_evaluation"
      ? "Pre-evaluation payment successful!"
      : "Publication payment successful!";
  };

  const getMessage = () => {
    const amountStr = amountPaid !== null ? formatAmount(amountPaid) : null;
    if (userType === "buyer") {
      if (uiLanguage === "fr") {
        return amountStr
          ? `Votre paiement de ${amountStr} a été traité avec succès. Votre abonnement ${planName} est maintenant actif.`
          : `Votre abonnement ${planName} a été activé avec succès.`;
      }
      return amountStr
        ? `Your payment of ${amountStr} has been processed successfully. Your ${planName} subscription is now active.`
        : `Your ${planName} subscription has been activated successfully.`;
    }
    if (userType === "consultant") {
      if (uiLanguage === "fr") {
        return amountStr
          ? `Votre paiement de ${amountStr} a été traité. Votre abonnement annuel au Programme Consultant est actif.`
          : "Votre abonnement annuel au Programme Consultant a été activé avec succès.";
      }
      return amountStr
        ? `Your payment of ${amountStr} has been processed. Your annual Consultant Program subscription is now active.`
        : "Your annual Consultant Program subscription has been activated successfully.";
    }
    // seller / broker
    if (uiLanguage === "fr") {
      return amountStr
        ? `Votre paiement de ${amountStr} a été traité avec succès.${paymentStep === "publication"
            ? " Votre deal sera bientôt publié sur la plateforme."
            : " Votre deal est maintenant soumis pour pré-évaluation."}`
        : "Votre paiement a été traité avec succès.";
    }
    return amountStr
      ? `Your payment of ${amountStr} has been processed successfully.${paymentStep === "publication"
          ? " Your deal will be published on the platform shortly."
          : " Your deal is now submitted for pre-evaluation by our team."}`
      : "Your payment has been processed successfully.";
  };

  const getNextStep = () => {
    if (userType === "buyer" || userType === "consultant") {
      return uiLanguage === "fr"
        ? "📧 Veuillez consulter vos courriels pour confirmer votre adresse et activer votre compte. Vérifiez aussi votre dossier de courrier indésirable."
        : "📧 Please check your email to confirm your email address and activate your account. Also check your spam folder.";
    }
    if (paymentStep === "pre_evaluation") {
      return uiLanguage === "fr"
        ? "Notre équipe examinera votre soumission et vous contactera dans les 48 heures."
        : "Our team will review your submission and contact you within 48 hours.";
    }
    return uiLanguage === "fr"
      ? "Votre deal apparaîtra sur le Deal Monitor une fois approuvé par notre équipe."
      : "Your deal will appear on the Deal Monitor once approved by our team.";
  };

  const getDashboardLabel = () => {
    const labels: Record<string, { en: string; fr: string }> = {
      seller: { en: "Go to Seller Dashboard", fr: "Accéder au tableau de bord Vendeur" },
      broker: { en: "Go to Broker Dashboard", fr: "Accéder au tableau de bord Courtier" },
      consultant: { en: "Go to Consultant Dashboard", fr: "Accéder au tableau de bord Consultant" },
      buyer: { en: "Go to Buyer Dashboard", fr: "Accéder au tableau de bord Acheteur" },
      partner: { en: "Go to Partner Dashboard", fr: "Accéder au tableau de bord Partenaire" },
    };
    const label = labels[userType] || labels.seller;
    return uiLanguage === "fr" ? label.fr : label.en;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[hsl(var(--navy))] via-[hsl(var(--navy-secondary))] to-[hsl(var(--navy-dark))] flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-card rounded-xl shadow-xl p-8 text-center border-t-4 border-green-500">
        <div className="mb-6">
          <div className="mx-auto w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
            {dealSubmitting ? (
              <Loader2 className="w-10 h-10 text-green-500 animate-spin" />
            ) : (
              <CheckCircle className="w-10 h-10 text-green-500 animate-bounce" />
            )}
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">{getTitle()}</h1>
        </div>

        {dealSubmitting && (
          <p className="text-sm text-muted-foreground mb-4 p-3 bg-primary/10 rounded-lg">
            {uiLanguage === "fr" ? "⏳ Soumission de votre dossier en cours..." : "⏳ Submitting your deal..."}
          </p>
        )}

        <p className="text-muted-foreground mb-4">{getMessage()}</p>

        <p className="text-sm text-muted-foreground mb-6 p-3 bg-muted/50 rounded-lg italic">
          {getNextStep()}
        </p>

        <div className="space-y-3">
          <Button onClick={() => navigate(getDashboardPath())} className="w-full" disabled={dealSubmitting}>
            {getDashboardLabel()} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Button
            variant="secondary"
            onClick={handleResendEmail}
            className="w-full"
            disabled={resending || dealSubmitting || cooldown > 0}
          >
            {resending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Mail className="mr-2 h-4 w-4" />
            )}
            {cooldown > 0
              ? uiLanguage === "fr"
                ? `Patientez ${cooldown}s avant un nouvel envoi`
                : `Wait ${cooldown}s before resending`
              : uiLanguage === "fr"
              ? "Renvoyer le courriel de confirmation"
              : "Resend confirmation email"}
          </Button>

          {resendStatus !== "idle" && (
            <div
              className={`text-sm p-3 rounded-lg text-left border ${
                resendStatus === "success"
                  ? "bg-green-500/10 border-green-500/30 text-green-700 dark:text-green-400"
                  : resendStatus === "error"
                  ? "bg-destructive/10 border-destructive/30 text-destructive"
                  : "bg-muted border-border text-muted-foreground"
              }`}
              role="status"
              aria-live="polite"
            >
              <strong className="block mb-1">
                {resendStatus === "pending" && (uiLanguage === "fr" ? "⏳ En attente..." : "⏳ Pending...")}
                {resendStatus === "success" && (uiLanguage === "fr" ? "✅ Succès" : "✅ Success")}
                {resendStatus === "error" && (uiLanguage === "fr" ? "❌ Erreur" : "❌ Error")}
              </strong>
              <span className="break-words">{resendMessage}</span>
            </div>
          )}
          <Button variant="outline" onClick={() => navigate("/")} className="w-full" disabled={dealSubmitting}>
            {uiLanguage === "fr" ? "Retour à l'accueil" : "Back to Home"}
          </Button>
        </div>
      </div>
    </div>
  );
}